/**
 * @fileoverview Input/output schemas for the chapter-drafter-enhanced-v2 agent.
 *
 * This composite agent chains a neutral chapter drafter → style-aligner-v2:
 *   1. chapter-drafter-v2-neutral (or single-chapter-drafter for short-form) —
 *      writes deliberately neutral prose focused on continuity, pacing, beats.
 *   2. style-aligner-v2 — applies the active style guide to that draft.
 *
 * The schema bundles inputs needed by both steps. styleGuide,
 * writingStyleSamples (corpus, from project.writingSamples), and the
 * ad-hoc writingStyleSample (UI override) are routed to the aligner.
 * Story-structural fields (chapterContext, latestKnownPlotState, etc.) are
 * consumed by the drafter.
 */

import { z } from 'zod';
import { CompositeBaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// HOOK INPUT SCHEMA
// =============================================================================

/**
 * Minimal input from UI/hooks - just what the user provides.
 */
export const HookInputSchema = z.object({
  chapterId: z.string().describe('The ID of the chapter to draft'),
  userMessage: z.string().describe('User instructions for drafting'),
  styleGuide: z.string().optional().describe('Optional style guide content'),
});

export type HookInput = z.infer<typeof HookInputSchema>;

// =============================================================================
// PER-STEP MODEL CONFIG SCHEMA
// =============================================================================

const ModelConfigSchema = z.object({
  modelProvider: z.string().optional(),
  modelName: z.string().optional(),
}).optional();

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow - includes all context needed for both steps.
 */
export const InputSchema = z.object({
  ...CompositeBaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.nextChapterContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.scenes.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterPovCharacter.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.styleGuide.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.seriesContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.latestKnownPlotState.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.lastThreeChaptersContent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.charactersCurrent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.worldElements.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.writingStyleSample.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.writingStyleSamples.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.userMessage.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.plotThreadStatus.schema.shape,
  /** Per-step model config for the drafter step (from UI control panel) */
  draftModelConfig: ModelConfigSchema,
  /**
   * Per-step model config for the second step (from UI control panel).
   *
   * Historically named `refineModelConfig` from when step 2 was draft-refiner-v2;
   * step 2 is now style-aligner-v2 but the field name is kept for caller stability.
   */
  refineModelConfig: ModelConfigSchema,
  /**
   * Whether this is a short-form project (single-chapter story).
   *
   * When true, step 1 falls back to single-chapter-drafter (which is still
   * style-aware). For long-form, step 1 is chapter-drafter-v2-neutral.
   */
  isShortForm: z.boolean().optional().describe('When true, dispatches to single-chapter-drafter for step 1 instead of chapter-drafter-v2-neutral'),
}).partial({
  scenes: true,
  nextChapterContext: true,
  plotThreadStatus: true,
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Output includes the final refined content plus combined commentary from both steps.
 */
export const OutputSchema = z.object({
  content: z.string().describe(
    'The final refined chapter content, formatted as a single HTML string.'
  ),
  commentary: z.string().describe(
    'Combined commentary from both drafting and refinement steps, explaining ' +
    'the creative choices and improvements made.'
  ),
  /** Step-level details for debugging/history */
  steps: z.object({
    draft: z.object({
      commentary: z.string(),
      durationMs: z.number(),
    }),
    refine: z.object({
      commentary: z.string(),
      durationMs: z.number(),
    }),
  }).optional().describe('Detailed results from each pipeline step'),
});

export type Output = z.infer<typeof OutputSchema>;
